
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { DatePickerPresets, GlobalFunctions, makeDateSmall } from '@global/GlobalFunctions';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { CmmsService, ICaseFile, iCauseTypes, iTriggerTypes } from '@services/cmms.service';
import { IAccount } from '@services/accounts.service';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';

/** FilterQueryParams */
interface FilterQueryParams {
	from?: number;
	to?: number;
	open?: boolean;
	closed?: boolean;
	CMMS?: boolean;
	NotCMMS?: boolean;
	all?: boolean;

	filter?: string;
	pageIndex?: number;
	pageSize?: number;
}

/** SAPCaseListComponent */
@Component({
	selector: 'app-sap-case-list',
	templateUrl: './sap-case-list.component.html',
	styleUrl: './sap-case-list.component.scss'
})
export class SAPCaseListComponent implements OnInit, OnDestroy, IQueryParameterFiltering<FilterQueryParams> {

	// =========================================================================
	// Query Parameter Filtering
	// =========================================================================

	/**
	 * Query Parameter List to use for filtering
	 *
	 * filter Query Params available to use
	 */
	public filterQueryParams: FilterQueryParams = {};

	/**
	 * filterQueryParamTypes
	 *
	 * Deffenition of the Query Parameter data types
	 */
	public filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		// accountGUID: 'string',
		// organisationGUID: 'string',
		from: 'number',
		to: 'number',
		open: 'boolean',
		closed: 'boolean',
		CMMS: 'boolean',
		NotCMMS: 'boolean',
		all: 'boolean',

		filter: 'string',
		pageIndex: 'number',
		pageSize: 'number',

	};

	// =========================================================================
	// Account / Organisation Context
	// =========================================================================

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** currentUser */
	public currentUser: WebAppUser;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	// =========================================================================
	// Component State
	// =========================================================================

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** titleText */
	public titleText: string = "SAP Case History";

	/** useTable */
	public useTable: boolean = true;

	/** isFiltering */
	public isFiltering: boolean = false;

	// =========================================================================
	// Case Data
	// =========================================================================

	/** cases */
	public cmmsCases: ICaseFile[] = [];

	/** filteredActions */
	public filteredActions: ICaseFile[] = [];

	/** triggerTypes - values correspond to value in db table */
	public triggerTypes: iTriggerTypes[] = [];

	/** triggerTypes - values correspond to value in db table */
	public causeTypes: iCauseTypes[] = [];

	// =========================================================================
	// Table Configuration
	// =========================================================================

	/** fields */
	public fields: { text: string, value: string } = { text: "ActionType", value: "ActionID" };

	/** displayedColumns */
	displayedColumns: string[] = ['IsOpen', 'InCMMS', 'AssetID', 'Trigger', 'OpenedAt', 'Description', 'CMMSCreatedAt', 'Cause'];

	/** datasource */
	public dataSourceCases: MatTableDataSource<ICaseFile>;

	// =========================================================================
	// Paginator / Sort
	// =========================================================================

	private _paginatorCases: MatPaginator;

	@ViewChild('paginatorCases')
	set paginatorCases(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.pageSize) { paginator.pageSize = this.filterQueryParams.pageSize }
			if (this.filterQueryParams.pageIndex) { paginator.pageIndex = this.filterQueryParams.pageIndex }

			this._paginatorCases = paginator;

			if (this.dataSourceCases) {
				this.dataSourceCases.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Webhook paginator:', error);
		}
	}

	private _sortCases: MatSort;

	@ViewChild('sortCases')
	set sortCases(sort: MatSort) {
		try {
			if (!sort) { return; }

			this._sortCases = sort;

			if (this.dataSourceCases) {
				this.dataSourceCases.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Actions sort:', error);
		}
	}

	// =========================================================================
	// Date / Query Range
	// =========================================================================

	/** Define start date */
	public startDate: Date = new Date();

	/** Define end date */
	public endDate: Date = new Date();

	/** queryFrom */
	private queryFrom: number = 0;

	/** queryTo */
	private queryTo: number = 0;

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** formattedStartDate */
	formattedStartDate: string | undefined;

	/** formattedEndDate */
	formattedEndDate: string | undefined;

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	// =========================================================================
	// Constructor
	// =========================================================================

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions SiteSubscriptions
	 * @param router router
	 * @param route ActivatedRoute,
	 * @param globalFunctions globalFunctions
	 * @param cmmsService CmmsService
	 */
	constructor(
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private cmmsService: CmmsService
	) {
		this.today.setHours(0, 0, 0, 0);
	}

	// =========================================================================
	// Lifecycle
	// =========================================================================

	/**
	 * ngOnInit
	 * Initializes the component by subscribing to various observables that provide necessary data such as account and organization information,
	 * hierarchical data, current user details, and query parameters.
	 * It uses `combineLatest` to ensure that all required data is available before proceeding with the initialization logic.
	 * The function also includes error handling to manage any issues that arise during the subscription process.
	 * Additionally, it sets up a subscription to query parameter changes to handle filtering of cases based on user-selected criteria.
	 */
	ngOnInit() {

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.cmmsService.getCauseTypes$().pipe(distinctUntilChanged()),
			this.cmmsService.getTriggerTypes$().pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, currentUser, causeTypes, triggerTypes, queryParamMap]) =>
				!!hierarchicalData && !!currentUser && !!causeTypes && !!triggerTypes && !!queryParamMap)
		).subscribe({
			next: async ([hierarchicalData, currentUser, causeTypes, triggerTypes, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.selectedAccount = selectedAccount;
				this.accountGUID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('actions') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.hierarchy = JSON.parse(hierarchicalData);

				this.causeTypes = causeTypes;
				this.triggerTypes = triggerTypes;

				this.currentUser = currentUser;

				/** Default to look for only open cases */
				this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
					queryParamMap,
					this.filterQueryParamTypes
				);

				this.filterQueryParams.open = this.filterQueryParams.open ?? true;
				this.filterQueryParams.CMMS = this.filterQueryParams.CMMS ?? false;
				this.filterQueryParams.closed = this.filterQueryParams.closed ?? false;
				this.filterQueryParams.NotCMMS = this.filterQueryParams.NotCMMS ?? false;
				this.filterQueryParams.all = !this.filterQueryParams.open && !this.filterQueryParams.closed && !this.filterQueryParams.CMMS && !this.filterQueryParams.NotCMMS;

				if (this.filterQueryParams.from) {
					this.filterQueryParams.to = this.filterQueryParams.to ?? 0;
					this.startDate = new Date(this.filterQueryParams.from);
					this.endDate = new Date(this.filterQueryParams.to ?? 0);
				} else {
					this.startDate = new Date(this.today);
					this.endDate = new Date(this.today);

					this.startDate.setDate(this.startDate.getDate() - 30);
					this.endDate.setDate(this.endDate.getDate() + 1);

					this.filterQueryParams.from = this.startDate.getTime();
					this.filterQueryParams.to = this.endDate.getTime();
				}

				if (this.queryTo != this.filterQueryParams.to || this.queryFrom != this.filterQueryParams.from) {
					this.getCases(this.filterQueryParams.from, this.filterQueryParams.to);
				}

				this.queryTo = this.filterQueryParams.to;
				this.queryFrom = this.filterQueryParams.from ?? 0;
				this.formattedStartDate = makeDateSmall(new Date(this.queryFrom));
				this.formattedEndDate = makeDateSmall(new Date(this.queryTo));

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	// =========================================================================
	// Data Loading
	// =========================================================================

	/**
	 * getActions
	 * Retrieves the list of cases from the CMMS service based on the provided time range.
	 * It uses combineLatest to make parallel API calls to fetch cases, cause types,
	 * and trigger types, ensuring that all necessary data is available before processing.
	 * The function also includes error handling to manage any issues that arise during the API calls,
	 * and it updates the component's state with the retrieved data for display in the UI.
	 * @param timeFrom timeFrom
	 * @param timeTo timeTo
	 */
	async getCases(timeFrom: number = 0, timeTo: number = 0) {

		const queryParams: any = {
			...(timeFrom !== 0 && { From: timeFrom }),
			...(timeTo !== 0 && { To: timeTo })
		};

		this.cmmsCases = [];

		try {
			this.assignResultsToDataSource();

			const cmmsCases = await firstValueFrom(
				this.cmmsService.getCases$(this.accountGUID, this.organisationUID, queryParams)
			);

			this.cmmsCases = cmmsCases;
			this.assignResultsToDataSource();
			this.applyQueryParameters();
		}
		catch (error: any) {
			// Handle the error
			if (error?.status === 404) {
				this.globalFunctions.HandleError(error, "this.cmmsService.getCases$: No cases found for the specified criteria.");
			} else {
				this.globalFunctions.HandleError(error, "this.cmmsService.getCases$: An error occurred while retrieving cases.");
			}
		}

	}

	// =========================================================================
	// Data Source Configuration
	// =========================================================================

	/**
	 * assignResultsToDataSource
	 * Initializes the MatTableDataSource with the results.
	 * It also defines a custom sortingDataAccessor to handle sorting based on specific fields, ensuring that null or undefined
	 * values are handled gracefully.
	 * This function is called to apply filtering the results to update the table display with the current set of filtered data.
	 */
	assignResultsToDataSource() {

		if (!this.dataSourceCases) {
			this.dataSourceCases = new MatTableDataSource(this.cmmsCases);
		} else {
			this.dataSourceCases.data = this.cmmsCases;
		}

		this.dataSourceCases.filterPredicate = (data: ICaseFile, filter: string) => {
			/** Build a string per row to filter and convert to lower case */

			const showOpen = this.filterQueryParams.open === true;
			const showClosed = this.filterQueryParams.closed === true;
			const showCMMS = this.filterQueryParams.CMMS === true;
			const hideCMMS = this.filterQueryParams.NotCMMS === true;

			const statusFilterActive = showOpen || showClosed;

			if (
				statusFilterActive &&
				!(
					(showOpen && data.IsOpen) ||
					(showClosed && !data.IsOpen)
				)
			) {
				return false;
			}

			const cmmsFilterActive = showCMMS || hideCMMS;

			if (
				cmmsFilterActive &&
				!(
					(showCMMS && data.InCMMS) ||
					(hideCMMS && !data.InCMMS)
				)
			) {
				return false;
			}

			const query = filter?.toLowerCase().trim() ?? '';

			if (!query) { return true; }

			// Combine the necessary fields for filtering
			const combinedStr = [
				convertEpochToDateString(data.OpenedAt),
				data.Asset.Name,
				data.Description,
				this.getTriggerTypeFromValue(data.TriggerType),
				data.FL
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query);

		};

		this.dataSourceCases.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'FL': return item.FL || ''; // Ensure no null/undefined values
				case 'AssetID': return item.Asset.Name || ''; // Ensure no null/undefined values
				case 'Cause': return item.Cause || ''; // Use AssetName for the 'asset' column
				case 'OpenedAt': return item.OpenedAt || '';
				case 'CMMSCreatedAt': return item.CMMSCreatedAt || 0;
				default: return '';
			}
		};

	}

	// =========================================================================
	// Query Parameter / Filter Handling
	// =========================================================================

	/**
	 * onQueryParameterChange
	 * Handles changes to the filter checkboxes and updates the filtered cases accordingly.
	 * When the "All" filter is selected, it deselects the "Open", "Closed", and "In CMMS" filters.
	 * When any of the "Open", "Closed", or "In CMMS" filters are changed, it deselects the "All" filter if necessary.
	 * After updating the filter states, it calls the filterCases() method to apply the filters to the case list.
	 * @param changed
	 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		if (changed == "filter") {
			event.cancel = true;
			const query = event.target.value.toLowerCase();
			this.filterQueryParams.filter = query;
			this.dataSourceCases.filter = query;
			this.applyQueryParameters();
		}
		else if (changed === 'all' && this.filterQueryParams.all) {
			this.filterQueryParams.open = false;
			this.filterQueryParams.closed = false;
			this.filterQueryParams.CMMS = false;
			this.filterQueryParams.NotCMMS = false;
		}
		else if (changed !== 'all') {
			this.filterQueryParams.all = false;

			if (
				!this.filterQueryParams.open &&
				!this.filterQueryParams.closed &&
				!this.filterQueryParams.CMMS &&
				!this.filterQueryParams.NotCMMS
			) {
				this.filterQueryParams.all = true;
			}
		} else {

		}

		// if (this.paginator) { this.paginator.pageIndex = 0; }

		this.applyQueryParameters();
	}

	/**
	 * DatePickerChange
	 * @param event
	 */
	DatePickerChange(event: any) {
		try {
			event.startDate.setHours(0, 0, 0, 0);
			event.endDate.setHours(0, 0, 0, 0);

			if (event.daySpan == 0) {
				this.startDate.setDate(this.today.getDate() - 30);
				this.endDate = this.today;
				this.filterQueryParams.to = this.endDate.getTime();
				this.filterQueryParams.from = this.startDate.getTime();

				if (this.queryTo != this.filterQueryParams.to || this.queryFrom != this.filterQueryParams.from) {
					this.applyQueryParameters(undefined, true);
				}

				return;
			}

			this.isFiltering = true;

			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				this.startDate = event.startDate;
				this.endDate = event.endDate;

				if (this.startDate && this.endDate) {
					this.formattedStartDate = makeDateSmall(this.startDate);
					this.formattedEndDate = makeDateSmall(this.endDate);

					/** Add a day to retrieve data for last day too */
					this.endDate.setDate(this.endDate.getDate() + 1);

					this.filterQueryParams.from = this.startDate.getTime();
					this.filterQueryParams.to = this.endDate.getTime();

					if (this.queryTo != this.filterQueryParams.to || this.queryFrom != this.filterQueryParams.from) {
						this.applyQueryParameters(undefined, true);
					}
				}
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false);
		}
	}

	/**
	 * Rebuild an URL for refreshing page with new query params
	 * This function uses Angular's Router to navigate to the current route while merging the existing query parameters with the new filter parameters.
	 * By calling this function after applying filters, the URL is updated to reflect the current filter state, allowing users to bookmark or share the filtered view.
	 * It also ensures that the filter state is preserved when the page is reloaded, providing a consistent user experience.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			this.router.navigate([], { queryParams: this.filterQueryParams, queryParamsHandling: 'merge' });
		} else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}
	}

	/**
	 * filterCases
	 * Applies the selected filters to the list of cases and updates the filtered cases accordingly.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {
		
		if (this.dataSourceCases) {
			const currentFilter = this.dataSourceCases.filter;
			this.dataSourceCases.filter = '';
			this.dataSourceCases.filter = currentFilter || this.filterQueryParams.filter || ' ';
		}

		this.isFiltering = !this.filterQueryParams.all || this.isFiltering;

		this.rebuildURL(reQuery);
	}

	// =========================================================================
	// Pagination
	// =========================================================================

	/**
	 * onPageChange
	 */
	onPageChange(event: PageEvent): void {
		this.filterQueryParams.pageSize = this.paginatorCases.pageSize;
		this.filterQueryParams.pageIndex = this.paginatorCases.pageIndex;
		this.applyQueryParameters();
	}

	// =========================================================================
	// Display / Lookup Helpers
	// =========================================================================

	/**
	 * getTriggerTypeFromValue
	 * Takes a numeric value representing a trigger type and returns the corresponding description by searching through the list of trigger types.
	 * The function uses the `find` method to locate the trigger type object with a matching `TriggerType` value and retrieves its `Description`.
	 * If a matching trigger type is found, its description is returned; otherwise, an empty string is returned.
	 * This function is useful for translating numeric trigger type values from the database into human-readable descriptions for display in the UI.
	 * @param value value
	 * @returns
	 */
	getTriggerTypeFromValue(value: number): string {
		let result = this.triggerTypes.find((x: iTriggerTypes) => x.TriggerType == value)?.Description;

		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * getCauseTypeFromValue
	 * Takes a numeric value representing a cause type and returns the corresponding description by searching through the list of cause types.
	 * The function uses the `find` method to locate the cause type object with a matching `CauseType` value and retrieves its `Description`.
	 * If a matching cause type is found, its description is returned; otherwise, an empty string is returned.
	 * This function is useful for translating numeric cause type values from the database into human-readable descriptions for display in the UI.
	 * @param value value
	 * @returns
	 */
	getCauseTypeFromValue(value: number): string {
		let result = this.causeTypes.find((x: iCauseTypes) => x.CauseType == value)?.Description;

		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * convertEpoch
	 *
	 * Converts an epoch timestamp to a human-readable date string using a global function.
	 * This function serves as a wrapper around the global `convertEpochToDateString` function,
	 * allowing for consistent date formatting throughout the component.
	 * By centralizing the date conversion logic in a global function,
	 * it ensures that all dates are displayed in the same format and makes it easier to
	 * maintain and update the date formatting logic in one place if needed.
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	// =========================================================================
	// Navigation
	// =========================================================================

	/**
	 * goToCaseDetails
	 * Navigates to the case details page for the selected case file.
	 * It uses Angular's Router to navigate to a route based on the case file's ID, relative to the current route.
	 * This allows users to view more detailed information about a specific case when they click on it in the list.
	 * @param caseFile action
	 */
	goToCaseDetails(caseFile: ICaseFile) {
		this.router.navigate([`${caseFile.Id}`], { relativeTo: this.route });
	}

	// =========================================================================
	// Hierarchy Helpers
	// =========================================================================

	/**
	 * Getting list of assets from an input hierarchy
	 * This function takes a hierarchical structure of organizations and their associated assets,
	 * and returns a flattened list of all assets contained within that hierarchy. It recursively traverses the hierarchy,
	 * checking each level for assets and adding them to the result list. If an item in the hierarchy has an 'Assets'
	 * property with one or more assets, those assets are added to the result. The function then continues to check any
	 * 'SubChild' items for additional assets, ensuring that all assets from all levels of the hierarchy are included in the final output.
	 * @param hierarchy OrganisationsHierarchy[]
	 * @returns any[]
	 */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}
}
